// @ts-nocheck
import { normalizeMyPhone, isMyPhone } from "@kawkaw/shared";
import { recentApiError } from "../api/apiErrorDetail";
import { useState, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Button, Drawer, Form, Input, Select, message,
  Avatar, Dropdown, Modal, Table,
} from "../components/admin-ui";
import type { MenuProps } from "../components/admin-ui";
import type { ColumnsType } from "../components/admin-ui";
import {
  PlusOutlined, UserOutlined, SafetyCertificateOutlined,
  DownloadOutlined, KeyOutlined, MoreOutlined, InfoCircleOutlined,
  TeamOutlined, CheckCircleOutlined, CloseCircleOutlined, SearchOutlined,
  CalendarOutlined,
} from "../components/admin-icons";
import dayjs from "dayjs";
import type { Staff } from "@kawkaw/shared";
import { DataTable } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { AdminShell } from "../layouts/AdminShell";
import {
  useAdminStaff, useAdminStalls, useCreateStaff, useUpdateStaff,
  useResetStaffPin, useDeleteStaff, useStaffSessions,
} from "../api/hooks";
import { tokens } from "../styles/tokens";
import { useConfirmDialog } from "../components/ConfirmDialog";
const { color: T } = tokens;
import { useAuthStore } from "../store/auth";
import { formatDateOnly, formatMonthDay, formatOrderTime, formatMytStamp } from "../utils/formatLocaleDate";
import { getInitials, getAvatarColor, formatLastLogin } from "../utils/staffDisplay";

/* ─── Login-history row shape (transformed from the real API response) ──── */
type HistorySession = {
  date: string; tablet: string; login: string;
  logout: string; duration: string; status: "online" | "completed";
};

function formatDuration(minutes: number | null): string {
  if (minutes == null) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/* ─── Login History Modal ─────────────────────────────────── */
function LoginHistoryModal({ staff, open, onClose }: {
  staff: Staff | null; open: boolean; onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [period, setPeriod] = useState<"today" | "week" | "month">("month");
  const { data, isLoading } = useStaffSessions(staff?.id);
  const rawSessions = data?.data ?? [];

  if (!staff) return null;

  const periodStart = period === "today" ? dayjs().startOf("day") : period === "week" ? dayjs().subtract(7, "day") : dayjs().subtract(30, "day");
  const filtered = rawSessions.filter((s) => dayjs(s.login_at).isAfter(periodStart));

  const sessions: HistorySession[] = filtered.map((s) => ({
    date: formatMonthDay(s.login_at, i18n.language),
    tablet: s.device_label ?? "—",
    login: formatOrderTime(s.login_at, i18n.language),
    logout: s.logout_at ? formatOrderTime(s.logout_at, i18n.language) : "—",
    // a session still open has no stored duration: show how long it has been running
    duration: formatDuration(s.duration_minutes ?? (s.status === "online" ? Math.max(0, dayjs().diff(dayjs(s.login_at), "minute")) : null)),
    status: s.status,
  }));

  const totalSessions = sessions.length;
  const hasOnline = filtered.some((s) => s.status === "online");
  const lastSeen = hasOnline
    ? t("staff.lastLoginToday", { time: formatOrderTime(dayjs(), i18n.language) })
    : filtered[0]
      ? formatMonthDay(filtered[0].login_at, i18n.language)
      : t("staff.none");
  const completedDurations = filtered.filter((s) => s.duration_minutes != null).map((s) => s.duration_minutes!);
  const avgDuration = completedDurations.length
    ? formatDuration(Math.round(completedDurations.reduce((a, b) => a + b, 0) / completedDurations.length))
    : "—";

  const historyColumns: ColumnsType<HistorySession> = [
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("staff.historyDate")}</span>,
      dataIndex: "date", key: "date", width: 70,
      render: (v: string) => <span style={{ fontWeight: 600, color: T.textDark }}>{v}</span>,
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("staff.historyTablet")}</span>,
      dataIndex: "tablet", key: "tablet", width: 80,
      render: (v: string) => <span style={{ color: T.textBody, fontSize: 13 }}>{v}</span>,
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("staff.historyLogin")}</span>,
      dataIndex: "login", key: "login", width: 90,
      render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span>,
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("staff.historyLogout")}</span>,
      dataIndex: "logout", key: "logout", width: 90,
      render: (v: string) => <span style={{ color: v === "—" ? T.borderLight : T.textDark }}>{v}</span>,
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("staff.historyDuration")}</span>,
      dataIndex: "duration", key: "duration", width: 90,
      render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span>,
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("staff.historyStatus")}</span>,
      dataIndex: "status", key: "status", width: 110,
      render: (v: "online" | "completed") => (
        <StatusBadge status={v} kind="staff" label={v === "online" ? t("staff.online") : t("staff.completed")} />
      ),
    },
  ];

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={680} centered
      styles={{ content: { borderRadius: 18, padding: 0, overflow: "hidden" } }}
      closable={false}>
      {/* Header */}
      <div style={{ padding: "22px 28px 18px", borderBottom: "1px solid #F3F4F6" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Avatar size={48} style={{ background: getAvatarColor(staff.name), fontSize: 16, fontWeight: 900, flexShrink: 0 }}>
              {getInitials(staff.name)}
            </Avatar>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18, fontWeight: 900, color: T.textDark }}>{staff.name}</span>
                <span style={{ padding: "2px 10px", borderRadius: 100, fontSize: 11, fontWeight: 700, background: T.surfaceSection, color: T.textBody }}>
                  {staff.role === "admin" || staff.role === "superadmin" ? t("staff.roleAdmin") : staff.role === "pos_operator" ? t("staff.posOperator") : t("staff.roleStaffLabel")}
                </span>
              </div>
              <div style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>{t("staff.loginHistoryTitle")}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", color: T.textMuted, fontSize: 22, lineHeight: 1 }}>×</button>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "20px 28px 24px" }}>
        {/* Stat cards */}
        <div style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 140, background: T.bg, borderRadius: 12, padding: "16px 18px" }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.textMuted, marginBottom: 6 }}>{t("staff.totalSessions")}</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: T.textDark }}>{totalSessions}</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
              {period === "today" ? t("staff.periodToday") : period === "week" ? t("staff.periodWeek") : t("staff.periodMonth")}
            </div>
            <div style={{ height: 3, background: T.brand, borderRadius: 2, marginTop: 8 }} />
          </div>
          <div style={{ flex: 1, minWidth: 140, background: T.bg, borderRadius: 12, padding: "16px 18px" }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.textMuted, marginBottom: 6 }}>{t("staff.avgDuration")}</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: T.textDark }}>{avgDuration}</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{t("staff.dailyAverage")}</div>
          </div>
          <div style={{ flex: 1, minWidth: 140, background: T.bg, borderRadius: 12, padding: "16px 18px" }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.textMuted, marginBottom: 6 }}>{t("staff.lastSeen")}</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: T.textDark, lineHeight: 1.4 }}>{lastSeen}</div>
          </div>
        </div>

        {/* Period tabs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
          {(["today", "week", "month"] as const).map(p => (
            <button key={p} type="button" onClick={() => setPeriod(p)} style={{
              padding: "7px 16px", borderRadius: 100, cursor: "pointer",
              border: period === p ? "1.5px solid #212121" : "1px solid #E5E7EB",
              background: T.surface,
              fontSize: 13, fontWeight: period === p ? 700 : 500,
              color: period === p ? T.textDark : T.textMuted,
            }}>
              {p === "today" ? t("staff.periodToday") : p === "week" ? t("staff.periodWeek") : t("staff.periodMonth")}
            </button>
          ))}
        </div>

        <Table<HistorySession>
          size="small"
          loading={isLoading}
          rowKey={(r) => `${r.date}-${r.login}`}
          dataSource={sessions}
          columns={historyColumns}
          locale={{ emptyText: t("staff.noSessions", "No login sessions in this period") }}
        />
      </div>

      {/* Footer */}
      <div style={{ padding: "14px 28px", borderTop: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Button onClick={onClose} style={{ height: 40, borderRadius: 10, fontWeight: 600, minWidth: 90 }}>{t("staff.close")}</Button>
        <Button icon={<DownloadOutlined />}
          onClick={() => {
            if (!sessions.length) { void message.info(t("staff.noSessions")); return; }
            const header = ["staff.historyDate", "staff.historyTablet", "staff.historyLogin", "staff.historyLogout", "staff.historyDuration", "staff.historyStatus"].map((k) => t(k));
            const csvRows = sessions.map((s) => [s.date, s.tablet, s.login, s.logout, s.duration, s.status === "online" ? t("staff.online") : t("staff.completed")]
              .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
            const csv = [header.join(","), ...csvRows].join("\n");
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${staff.name.replace(/\s+/g, "-")}-login-history-${formatMytStamp(new Date()).slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          style={{ height: 40, borderRadius: 10, fontWeight: 700, background: T.successText, borderColor: T.successText, color: T.surface }}>
          {t("staff.exportCsv")}
        </Button>
      </div>
    </Modal>
  );
}

/* ─── DrawerSection helper ────────────────────────────────── */
function DrawerSection({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div style={{ background: T.surface, borderRadius: 14, border: "1px solid #F3F4F6", padding: "18px 20px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <span style={{ color: T.brand, fontSize: 16 }}>{icon}</span>
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1, color: T.textDark }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

/* ─── Add / Edit Drawer ───────────────────────────────────── */
// Admin/superadmin accounts moved to AdminAccounts.tsx (/admins) — this
// screen only ever creates/edits operational staff now.
type DrawerRole = "stall_staff" | "pos_operator";
type DrawerFormValues = {
  name: string; phone: string; role: DrawerRole;
  stallId?: string; pin?: string; confirm_pin?: string;
};

// The shared `Select` (admin-ui.tsx) collapses an empty-string option value to
// `undefined` on change — indistinguishable from "field never touched" once it
// reaches the save handler, so a raw "" sentinel for "unassigned" silently
// fails to clear an existing assignment. Use a value the wrapper can't collapse.
const UNASSIGNED_STALL = "__unassigned__";

function AddEditDrawer({ open, editing, onClose, stallsData, onSaved }: {
  open: boolean; editing: Staff | null;
  onClose: () => void; stallsData: { id: string; name: string }[];
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm<DrawerFormValues>();
  const [selectedRole, setSelectedRole] = useState<DrawerRole>("stall_staff");
  const createStaff = useCreateStaff();
  const updateStaff = useUpdateStaff();
  const isEdit = !!editing;

  const ROLE_INFO: Record<DrawerRole, string> = useMemo(() => ({
    stall_staff: t("staff.roleInfoStall"),
    pos_operator: t("staff.roleInfoPos"),
  }), [t]);

  const handleAfterOpen = (visible: boolean) => {
    if (!visible) return;
    if (editing) {
      const r: DrawerRole = editing.role === "pos_operator" ? "pos_operator" : "stall_staff";
      setSelectedRole(r);
      form.setFieldsValue({ name: editing.name, phone: editing.phone, role: r, stallId: editing.stall_id ?? (r === "stall_staff" ? undefined : UNASSIGNED_STALL) });
    } else {
      setSelectedRole("stall_staff");
      form.resetFields();
      form.setFieldValue("role", "stall_staff");
    }
  };

  const onFinish = async (values: DrawerFormValues) => {
    try {
      const stallId = !values.stallId || values.stallId === UNASSIGNED_STALL ? "" : values.stallId;
      if (isEdit && editing) {
        await updateStaff.mutateAsync({ id: editing.id, name: values.name, role: values.role, stallId });
        void message.success(t("staff.updatedOk"));
      } else {
        await createStaff.mutateAsync({ name: values.name, phone: values.phone, role: values.role, stallId, pin: values.pin ?? "" });
        void message.success(t("staff.addedOk"));
      }
      onSaved();
    } catch {
      // the API client keeps the server's reason for a few seconds (api/apiErrorDetail.ts)
      // message.error appends that reason to "save failed"; a taken phone also gets an inline error on the field itself
      if (recentApiError()?.includes("手机号已被使用")) { form.setFields([{ name: "phone", errors: [t("staff.phoneInUse")] }]); return; }   // the field says it — no second toast with the same words
      void message.error(t("staff.saveFailed"));
    }
  };

  const loading = createStaff.isPending || updateStaff.isPending;

  return (
    <Drawer
      open={open} onClose={onClose} placement="right" width={440}
      closable={false} afterOpenChange={handleAfterOpen}
      styles={{
        body: { padding: "20px 20px 100px", background: T.bg },
        header: { display: "none" },
        footer: { padding: 0, border: "none" },
      }}
      footer={
        <div style={{ padding: "16px 20px", display: "flex", gap: 12, background: T.surface, borderTop: "1px solid #F3F4F6" }}>
          <Button onClick={onClose} style={{ flex: 1, height: 48, borderRadius: 12, fontWeight: 700, fontSize: 15 }}>{t("staff.cancel")}</Button>
          <Button type="primary" onClick={() => void form.submit()} loading={loading}
            style={{ flex: 2, height: 48, borderRadius: 12, fontWeight: 800, fontSize: 15, background: T.brand, borderColor: T.brand }}>
            {isEdit ? t("staff.saveChanges") : t("staff.saveStaff")}
          </Button>
        </div>
      }
    >
      {/* Custom header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 22, fontWeight: 900, color: T.textDark }}>{isEdit ? t("staff.editStaff") : t("staff.addStaff")}</span>
        <button type="button" onClick={onClose}
          style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 22, color: T.textMuted, lineHeight: 1 }}>×</button>
      </div>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        {/* Personal Info */}
        <DrawerSection icon={<UserOutlined />} title={t("staff.personalInfo")}>
          <Form.Item name="name"
            label={<span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>{t("staff.fullName")} <span style={{ color: T.brand }}>*</span></span>}
            rules={[{ required: true, message: t("staff.fullNameRequired") }]} style={{ marginBottom: 14 }}>
            <Input placeholder={t("staff.placeholderFullName")} style={{ height: 44, borderRadius: 10, fontSize: 14 }} />
          </Form.Item>
          <Form.Item name="phone"
            label={<span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>{t("staff.phoneNumber")} <span style={{ color: T.brand }}>*</span></span>}
            rules={[{ required: true, message: t("staff.phoneRequired") }, { validator: (_: unknown, v: string) => !v || (normalizeMyPhone(String(v)) && isMyPhone(normalizeMyPhone(String(v))!)) ? Promise.resolve() : Promise.reject(new Error(t("staff.phoneInvalid"))) }]} style={{ marginBottom: 0 }}>
            <Input placeholder={t("staff.placeholderPhone")} disabled={isEdit} style={{ height: 44, borderRadius: 10, fontSize: 14 }} />
          </Form.Item>
        </DrawerSection>

        {/* Role & Access */}
        <DrawerSection icon={<SafetyCertificateOutlined />} title={t("staff.roleAccess")}>
          <Form.Item name="role" style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", background: T.surfaceSection, borderRadius: 10, padding: 4 }}>
              {(["stall_staff", "pos_operator"] as const).map(r => (
                <button key={r} type="button"
                  onClick={() => { setSelectedRole(r); form.setFieldValue("role", r); if (r === "stall_staff" && form.getFieldValue("stallId") === UNASSIGNED_STALL) form.setFieldValue("stallId", undefined); }}
                  style={{
                    flex: 1, padding: "9px 0", borderRadius: 8, border: "none", cursor: "pointer",
                    fontSize: 14, fontWeight: 700,
                    background: selectedRole === r ? T.brand : "transparent",
                    color: selectedRole === r ? T.surface : T.textMuted,
                    transition: "all .15s",
                  }}>
                  {r === "stall_staff" ? t("staff.staff") : t("staff.posOperator")}
                </button>
              ))}
            </div>
          </Form.Item>
          <div style={{ background: T.brandLight, borderLeft: "3px solid #F97316", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: T.brandAmberText }}>
            {ROLE_INFO[selectedRole]}
          </div>
          <Form.Item name="stallId"
            label={<span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>{selectedRole === "pos_operator" ? t("staff.assignedStallPos") : t("staff.assignedTablet")}{selectedRole === "stall_staff" ? " *" : ""}</span>}
            rules={selectedRole === "stall_staff" ? [{ required: true, message: t("staff.stallRequired", "A stall must be assigned for stall staff accounts") }] : []}
            style={{ marginBottom: 0 }}>
            <Select style={{ height: 44 }} placeholder={selectedRole === "stall_staff" ? "请选择档口" : undefined}
              options={[
                ...(selectedRole === "stall_staff" ? [] : [{ value: UNASSIGNED_STALL, label: selectedRole === "pos_operator" ? t("staff.notAssignedAllStalls") : t("staff.notAssigned") }]),
                ...stallsData.map(s => ({ value: s.id, label: s.name })),
              ]}
            />
          </Form.Item>
        </DrawerSection>

        {/* Login PIN — create only */}
        {!isEdit && (
          <DrawerSection icon={<KeyOutlined />} title={t("staff.loginPin")}>
            <Form.Item name="pin"
              label={<span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, display: "block", textAlign: "center" }}>{t("staff.setupPin")}</span>}
              rules={[
                { required: true, message: t("staff.pinRequired") },
                { len: 4, message: t("staff.pinLen") },
                { pattern: /^\d+$/, message: t("staff.pinNumeric") },
              ]} style={{ marginBottom: 14 }}>
              <Input.Password maxLength={4} placeholder="• • • •"
                style={{ height: 56, borderRadius: 10, fontSize: 22, textAlign: "center", letterSpacing: 10 }} />
            </Form.Item>
            <Form.Item name="confirm_pin"
              label={<span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, display: "block", textAlign: "center" }}>{t("staff.confirmPin")}</span>}
              dependencies={["pin"]}
              rules={[
                { required: true, message: t("staff.confirmPinRequired") },
                ({ getFieldValue }) => ({
                  validator: (_, value) =>
                    !value || getFieldValue("pin") === value
                      ? Promise.resolve()
                      : Promise.reject(new Error(t("staff.pinMismatch"))),
                }),
              ]} style={{ marginBottom: 14 }}>
              <Input.Password maxLength={4} placeholder="• • • •"
                style={{ height: 56, borderRadius: 10, fontSize: 22, textAlign: "center", letterSpacing: 10 }} />
            </Form.Item>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: T.textMuted }}>
              <InfoCircleOutlined style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{t("staff.pinHint")}</span>
            </div>
          </DrawerSection>
        )}
      </Form>
    </Drawer>
  );
}

/* ─── Reset PIN Modal ─────────────────────────────────────── */
function ResetPinDrawer({ staff, open, onClose }: { staff: Staff | null; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ newPin: string; confirm: string }>();
  const resetPin = useResetStaffPin();

  const handleClose = () => { form.resetFields(); onClose(); };

  const onFinish = async (values: { newPin: string }) => {
    if (!staff) return;
    try {
      await resetPin.mutateAsync({ id: staff.id, newPin: values.newPin });
      void message.success(t("staff.pinResetOk"));
      form.resetFields();
      onClose();
    } catch {
      void message.error(t("staff.pinResetFailed"));
    }
  };

  return (
    <Drawer
      open={open} onClose={handleClose} placement="right" width={440}
      closable={false}
      afterOpenChange={(v) => { if (!v) form.resetFields(); }}
      styles={{
        body: { padding: "20px 20px 100px", background: T.bg },
        header: { display: "none" },
        footer: { padding: 0, border: "none" },
      }}
      footer={
        <div style={{ padding: "16px 20px", display: "flex", gap: 12, background: T.surface, borderTop: "1px solid #F3F4F6" }}>
          <Button onClick={handleClose} style={{ flex: 1, height: 48, borderRadius: 12, fontWeight: 700, fontSize: 15 }}>{t("staff.cancel")}</Button>
          <Button type="primary" onClick={() => void form.submit()} loading={resetPin.isPending}
            style={{ flex: 2, height: 48, borderRadius: 12, fontWeight: 800, fontSize: 15, background: T.brand, borderColor: T.brand }}>
            {t("staff.resetPinBtn")}
          </Button>
        </div>
      }
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 22, fontWeight: 900, color: T.textDark }}>{t("staff.resetPinTitle")}</span>
        <button type="button" onClick={handleClose}
          style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 22, color: T.textMuted, lineHeight: 1 }}>×</button>
      </div>

      {staff && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, background: T.surface, borderRadius: 12, border: "1px solid #F3F4F6", padding: "14px 16px", marginBottom: 20 }}>
          <Avatar size={40} style={{ background: getAvatarColor(staff.name), fontSize: 14, fontWeight: 900, flexShrink: 0 }}>
            {getInitials(staff.name)}
          </Avatar>
          <div>
            <div style={{ fontWeight: 700, color: T.textDark, fontSize: 14 }}>{staff.name}</div>
            <div style={{ fontSize: 12, color: T.textMuted }}>{staff.phone}</div>
          </div>
        </div>
      )}

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <DrawerSection icon={<KeyOutlined />} title={t("staff.newPinSection")}>
          <Form.Item name="newPin"
            label={<span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, display: "block", textAlign: "center" }}>{t("staff.newPin")}</span>}
            rules={[{ required: true, message: t("staff.pinRequired") }, { len: 4, message: t("staff.pinLenReset") }, { pattern: /^\d+$/, message: t("staff.numericOnly") }]}
            style={{ marginBottom: 14 }}>
            <Input.Password maxLength={4} placeholder="• • • •"
              style={{ height: 56, borderRadius: 10, fontSize: 22, textAlign: "center", letterSpacing: 10 }} />
          </Form.Item>
          <Form.Item name="confirm"
            label={<span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, display: "block", textAlign: "center" }}>{t("staff.confirmPin")}</span>}
            dependencies={["newPin"]}
            rules={[{ required: true, message: t("staff.confirmPinRequired") }, ({ getFieldValue }) => ({
              validator: (_, value) => !value || getFieldValue("newPin") === value ? Promise.resolve() : Promise.reject(new Error(t("staff.pinMismatch"))),
            })]}
            style={{ marginBottom: 14 }}>
            <Input.Password maxLength={4} placeholder="• • • •"
              style={{ height: 56, borderRadius: 10, fontSize: 22, textAlign: "center", letterSpacing: 10 }} />
          </Form.Item>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: T.textMuted }}>
            <InfoCircleOutlined style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{t("staff.resetPinHint")}</span>
          </div>
        </DrawerSection>
      </Form>
    </Drawer>
  );
}

/* ─── Screen ─────────────────────────────────────────────── */
type PeriodKey = "today" | "week" | "month";
// Admin/superadmin accounts live in AdminAccounts.tsx (/admins) now — this
// screen only ever shows operational staff, so the filter distinguishes
// between the two operational roles instead of admin-vs-staff.
type RoleFilter = "all" | "stall_staff" | "pos_operator";
type StatusFilter = "all" | "active" | "inactive";

function StaffStatCard({
  icon, label, value, note, tone,
}: {
  icon: ReactNode; label: string; value: number; note: string;
  tone: "total" | "active" | "inactive";
}) {
  return (
    <div className={`admin-kpi-card admin-staff-stat admin-staff-stat-${tone}`}>
      <div className="admin-kpi-heading flex">
        <div className="admin-kpi-label">{label}</div>
        <span className="admin-kpi-icon">{icon}</span>
      </div>
      <div className="admin-kpi-value">{value.toLocaleString()}</div>
      <div className="admin-staff-stat-note">{note}</div>
    </div>
  );
}

export function StaffScreen() {
  const { t, i18n } = useTranslation();
  const { confirm: confirmDialog } = useConfirmDialog();
  const [period, setPeriod] = useState<PeriodKey>("today");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<Staff | null>(null);
  const [historyStaff, setHistoryStaff] = useState<Staff | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pinStaff, setPinStaff] = useState<Staff | null>(null);
  const [pinOpen, setPinOpen] = useState(false);

  const { data, isLoading } = useAdminStaff();
  const { data: stallsData } = useAdminStalls();
  const deleteStaff = useDeleteStaff();
  const meId = useAuthStore((s) => s.staff?.id);

  // Admin/superadmin rows are managed on the separate Admin Accounts screen —
  // never shown here regardless of what the backend returns.
  const allStaff: Staff[] = (data?.data ?? []).filter(s => s.role === "pos_operator" || s.role === "stall_staff");
  const stalls = stallsData?.data ?? [];

  const totalStaff   = allStaff.length;
  const activeCount  = allStaff.filter(s => s.is_active).length;
  const inactiveCount = allStaff.filter(s => !s.is_active).length;

  const filteredStaff = useMemo(() => allStaff.filter(s => {
    if (search) {
      const q = search.toLowerCase();
      if (!s.name.toLowerCase().includes(q) && !s.phone.includes(q)) return false;
    }
    if (roleFilter !== "all" && s.role !== roleFilter) return false;
    if (statusFilter === "active"   && !s.is_active) return false;
    if (statusFilter === "inactive" &&  s.is_active) return false;
    return true;
  }), [allStaff, search, roleFilter, statusFilter]);

  const today = dayjs();
  const periodLabel = useMemo(() => {
    if (period === "today") return formatDateOnly(today, i18n.language);
    if (period === "week")  return `${formatMonthDay(today.subtract(6, "day"), i18n.language)} - ${formatDateOnly(today, i18n.language)}`;
    return `${formatMonthDay(today.startOf("month"), i18n.language)} - ${formatDateOnly(today, i18n.language)}`;
  }, [period, today, i18n.language]);

  const openCreate = () => { setEditingStaff(null); setDrawerOpen(true); };
  const openEdit   = (s: Staff) => { setEditingStaff(s); setDrawerOpen(true); };

  const columns: ColumnsType<Staff> = [
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted, letterSpacing: 0.5 }}>{t("staff.colStaffDetails")}</span>,
      key: "details", width: 220,
      render: (_, row) => (
        <div className="admin-staff-identity" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar size={38} style={{ background: getAvatarColor(row.name), fontSize: 13, fontWeight: 900, flexShrink: 0 }}>
            {getInitials(row.name)}
          </Avatar>
          <div className="admin-staff-identity-copy">
            <div className="admin-data-customer" style={{ fontWeight: 700, color: T.textDark, fontSize: 14 }}>{row.name}</div>
            <div className="admin-data-secondary admin-staff-phone" style={{ fontSize: 12, color: T.textMuted }}>{row.phone}</div>
          </div>
        </div>
      ),
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted, letterSpacing: 0.5 }}>{t("staff.colRole")}</span>,
      dataIndex: "role", key: "role", width: 110,
      render: (v: string) => {
        const isPos = v === "pos_operator";
        return (
          <span className="admin-data-badge admin-staff-role" style={{
            padding: "4px 14px", borderRadius: 100, fontSize: 12, fontWeight: 800,
            background: T.surfaceSection, color: T.textBody,
          }}>
            {isPos ? t("staff.posOperator") : t("staff.roleStaffLabel")}
          </span>
        );
      },
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted, letterSpacing: 0.5 }}>{t("staff.colAssignedTablet")}</span>,
      dataIndex: "stall_id", key: "tablet", width: 150,
      render: (v: string | null) => {
        const stallName = v ? stalls.find(s => s.id === v)?.name : null;
        return <span style={{ color: stallName ? T.textDark : T.textMuted, fontSize: 13 }}>{stallName ?? t("staff.notAssignedTablet")}</span>;
      },
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted, letterSpacing: 0.5 }}>{t("staff.colStatus")}</span>,
      dataIndex: "is_active", key: "status", width: 120,
      render: (v: boolean) => (
        <StatusBadge status={v ? "active" : "inactive"} kind="staff" label={v ? t("staff.statusActive") : t("staff.statusInactive")} />
      ),
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted, letterSpacing: 0.5 }}>{t("staff.colLastLogin")}</span>,
      dataIndex: "last_login_at", key: "lastLogin", width: 130,
      render: (v: string | null) => <span className="admin-data-time" style={{ fontSize: 13, color: T.textBody }}>{formatLastLogin(v, t, i18n.language)}</span>,
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted, letterSpacing: 0.5 }}>{t("staff.colActions")}</span>,
      key: "actions", width: 70, align: "center",
      render: (_, row) => {
        const items: MenuProps["items"] = [
          { key: "edit",    label: <span style={{ fontWeight: 600 }}>{t("staff.edit")}</span>,           onClick: () => openEdit(row) },
          { key: "history", label: <span style={{ fontWeight: 600 }}>{t("staff.loginHistory")}</span>,  onClick: () => { setHistoryStaff(row); setHistoryOpen(true); } },
          { key: "reset",   label: <span style={{ fontWeight: 600 }}>{t("staff.tooltipResetPin")}</span>, onClick: () => { setPinStaff(row); setPinOpen(true); } },
          { type: "divider" },
          {
            key: "delete", danger: true, disabled: row.id === meId,
            label: <span style={{ fontWeight: 600 }}>{t("staff.removeStaff")}</span>,
            onClick: async () => {
              const ok = await confirmDialog({
                title: t("staff.removeStaff"),
                description: row.name,
                confirmLabel: t("staff.removeStaff"),
                danger: true,
              });
              if (!ok) return;
              try {
                await deleteStaff.mutateAsync(row.id);
                void message.success(t("staff.removedOk"));
              } catch {
                void message.error(t("staff.removeFailed"));
              }
            },
          },
        ];
        return (
          <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
            <Button className="admin-staff-more admin-data-more" aria-label={t("staff.colActions")} type="text" icon={<MoreOutlined style={{ fontSize: 18 }} />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <AdminShell>
      <div className="admin-staff-page admin-list-page">
        <div className="admin-staff-page-inner">
      {/* Page header */}
      <div className="admin-staff-toolbar">
        <div>
          <h1 className="admin-page-title">{t("staff.managementTitle")}</h1>
          <p className="admin-staff-subtitle">{t("staff.pageSubtitle")}</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
          className="admin-staff-create">
          {t("staff.addStaffBtn")}
        </Button>
      </div>

      {/* Period tabs */}
      <div className="admin-staff-period admin-txn-filter-panel">
        <div className="admin-staff-period-options">
          {(["today", "week", "month"] as PeriodKey[]).map(p => (
            <button key={p} type="button" onClick={() => setPeriod(p)} aria-pressed={period === p}>
              {p === "today" ? t("staff.periodToday") : p === "week" ? t("staff.periodWeek") : t("staff.periodMonth")}
            </button>
          ))}
        </div>
        <span className="admin-staff-period-label"><CalendarOutlined />{periodLabel}</span>
      </div>

      {/* Stat cards */}
      <div className="admin-staff-stats admin-txn-stats grid">
        <StaffStatCard icon={<TeamOutlined />} label={t("staff.totalStaff")} value={totalStaff} note={t("staff.teamMembers")} tone="total" />
        <StaffStatCard icon={<CheckCircleOutlined />} label={t("staff.active")} value={activeCount} note={t("staff.currentlyWorking")} tone="active" />
        <StaffStatCard icon={<CloseCircleOutlined />} label={t("staff.inactive")} value={inactiveCount} note={t("staff.offDuty")} tone="inactive" />
      </div>

      {/* Filter bar */}
      <div className="admin-staff-filters admin-txn-filter-panel">
        <Input className="admin-staff-search" prefix={<SearchOutlined style={{ color: T.textMuted }} />}
          placeholder={t("staff.searchPlaceholder")} value={search} onChange={e => setSearch(e.target.value)} allowClear />
        <Select value={roleFilter} onChange={v => setRoleFilter(v as RoleFilter)}
          options={[{ value: "all", label: t("staff.allRoles") }, { value: "stall_staff", label: t("staff.staff") }, { value: "pos_operator", label: t("staff.posOperator") }]} />
        <Select value={statusFilter} onChange={v => setStatusFilter(v as StatusFilter)}
          options={[{ value: "all", label: t("staff.allStatus") }, { value: "active", label: t("staff.activeStatus") }, { value: "inactive", label: t("staff.inactiveStatus") }]} />
      </div>

      {/* Table */}
      <p className="admin-staff-results">{t("common.total", { count: filteredStaff.length })}</p>
      <div className="admin-staff-table-panel admin-data-table-panel">
        <DataTable<Staff>
          columns={columns} dataSource={filteredStaff} loading={isLoading} rowKey="id" sticky={false}
          footer={() => (
            <span style={{ fontSize: 12, color: T.textMuted }}>{t("staff.showingCount", { filtered: filteredStaff.length, total: totalStaff })}</span>
          )}
        />
      </div>

      {/* Drawer, Modals */}
      <AddEditDrawer open={drawerOpen} editing={editingStaff} onClose={() => setDrawerOpen(false)} stallsData={stalls} onSaved={() => setDrawerOpen(false)} />
      <LoginHistoryModal staff={historyStaff} open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <ResetPinDrawer staff={pinStaff} open={pinOpen} onClose={() => setPinOpen(false)} />
        </div>
      </div>
    </AdminShell>
  );
}
